import { useEffect, useState } from "react";
import { QA_IMPERSONATION_ENABLED } from "../lib/qaImpersonation";

const STORAGE_KEY = "nubrakes:uiv:v1";

/**
 * Reads the `?uiv=` UI-variant param and remembers it.
 *
 * There is no middleware here, so a query param is gone the moment the technician taps
 * the bottom nav — persisting means a variant only has to be handed out once, as a
 * link, and survives navigation, refreshes, and signing out. localStorage can throw in
 * a locked-down browser, so an unstorable variant just behaves as unset and the link
 * has to be opened again.
 */
const read = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const write = (value: string) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Nothing to do — the caller still gets the value for this page load.
  }
};

const clear = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Already unreadable, so there is nothing stored to contradict us.
  }
};

/**
 * `?uiv=parts` opts in, `?uiv=` with no value opts back out, and no param at all leaves
 * whatever was stored alone. Absent and empty have to stay distinguishable — reading
 * both as "nothing" would make opting out impossible, since every ordinary page in the
 * app is visited without the param.
 */
const paramFromUrl = (): { present: boolean; value: string | null } => {
  const raw = new URLSearchParams(window.location.search).get("uiv");
  if (raw === null) return { present: false, value: null };
  return { present: true, value: raw.trim() || null };
};

export const useUiv = (): string | null => {
  const [uiv, setUiv] = useState<string | null>(() => {
    const param = paramFromUrl();
    return param.present ? param.value : read();
  });

  // Runs once: the param only ever arrives on a full page load, because nothing inside
  // the app links to itself with it.
  useEffect(() => {
    const param = paramFromUrl();
    if (!param.present) return;
    if (param.value) write(param.value);
    else clear();
    setUiv(param.value);
  }, []);

  return uiv;
};

/** The variant that turns on the Inventory/Parts tab (NUB-359). */
export const PARTS_UIV = "parts";

/** The variant that turns on the Vehicle History tab (NUB-358). */
export const VEHICLE_HISTORY_UIV = "vh";

/** The Parts tab is released to every technician; `?uiv=parts` is no longer required. */
export const useHasPartsTab = (): boolean => true;

/**
 * Hides the Vehicle History tab from everyone except technicians handed the `?uiv=vh`
 * link, and opens it for everyone on a QA build. The gate is removed at general release.
 */
export const useHasVehicleHistory = (): boolean => {
  const uiv = useUiv();
  return QA_IMPERSONATION_ENABLED || uiv === VEHICLE_HISTORY_UIV;
};
